import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import UpperBarIn from "../UpperBar/UpperBarIn";
import './AuthCards.css';
import coverImage from "../../../images/couvImage.jpg";
import silverBadge from "../../../images/silverBadge.png";
import goldBadge from "../../../images/goldBadge.png";
import modulesIcon from "../../../images/moduleIcon.png";
import agendaIcon from "../../../images/agendaIcon.png";
import tasksIcon from "../../../images/tasksIcon.png";
import formationIcon from "../../../images/formationsIcon.png";
import competencesIcon from "../../../images/formationsIcon.png";
import performanceIcon from "../../../images/formationsIcon.png";
import recrutementIcon from "../../../images/formationsIcon.png";
import handbookIcon from "../../../images/formationsIcon.png";
import selfcareIcon from "../../../images/formationsIcon.png";
import { useNavigate } from 'react-router-dom';
import { setSelectedRole } from "../../../redux/actions/roleActions";
import { API_BASE_URL } from "../../../../api";
import { getUserRequest, updateMyProfileRequest, uploadUserPhotoRequest, deleteUserPhotoRequest, uploadAttestationRibRequest } from "../../../redux/actions/userActions";
import { getUserSoldesRequest } from "../../../redux/actions/leaveBalanceActions";
import { getMyLeaveRequestsRequest } from "../../../redux/actions/leaveRequestsActions";
import { getMyActivitySummaryRequest } from "../../../redux/actions/applicationLogActions";


/* ─── ALL AGENDA EVENTS ──────────────────────────────────────── */
const ALL_AGENDA_EVENTS = [
    { date: '2025-10-03', time: '09:00', title: "Kick-off projet Q4", type: 'meeting' },
    { date: '2025-10-08', time: '11:00', title: 'Revue des candidatures', type: 'interview' },
    { date: '2025-10-14', time: '14:00', title: 'Formation Excel avancé', type: 'training' },
    { date: '2025-10-17', time: '10:30', title: "Réunion d'équipe", type: 'meeting' },
    { date: '2025-10-21', time: '15:00', title: 'Entretien annuel', type: 'interview' },
    { date: '2025-10-28', time: '09:00', title: "Réunion d'équipe", type: 'meeting' },
    { date: '2025-10-28', time: '14:30', title: 'Entretien RH', type: 'interview' },
    { date: '2025-10-29', time: '10:00', title: 'Formation Leadership', type: 'training' },
    { date: '2025-10-30', time: '15:00', title: 'Point mensuel', type: 'meeting' },
    { date: '2026-03-04', time: '09:30', title: 'Réunion stratégie Q1', type: 'meeting' },
    { date: '2026-03-04', time: '14:00', title: 'Entretien candidat Dev', type: 'interview' },
    { date: '2026-03-10', time: '10:00', title: 'Formation Gestion de projet', type: 'training' },
    { date: '2026-03-12', time: '11:30', title: "Réunion d'équipe", type: 'meeting' },
    { date: '2026-03-17', time: '09:00', title: 'Point RH mensuel', type: 'meeting' },
    { date: '2026-03-19', time: '15:00', title: 'Entretien bilan mi-année', type: 'interview' },
    { date: '2026-03-24', time: '14:30', title: 'Atelier onboarding', type: 'training' },
    { date: '2026-03-27', time: '10:00', title: 'Revue de performance', type: 'meeting' },
    { date: '2026-03-31', time: '16:00', title: 'Clôture bilan Q1', type: 'meeting' },
];

/* ─── ALL TASKS DATA ─────────────────────────────────────────── */
const INITIAL_TASKS = [];

const getInitials = (firstName, lastName, fullName) => {
    const clean = (value) => (value || '').trim();
    const first = clean(firstName);
    const last = clean(lastName);

    if (first || last) {
        return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || 'U';
    }

    const parts = clean(fullName).split(/\s+/).filter(Boolean);
    return `${parts[0]?.charAt(0) || ''}${parts[1]?.charAt(0) || ''}`.toUpperCase() || 'U';
};

const getManagerDisplayName = (user) => {
    const directName = user?.managerFullName || user?.managerName;
    if (directName && String(directName).trim()) return directName;

    const manager = user?.manager;
    if (manager?.fullName && String(manager.fullName).trim()) return manager.fullName;

    const composed = `${manager?.firstName || ''} ${manager?.lastName || ''}`.trim();
    return composed || 'Aucun manager';
};

const isAnnualLeaveBalance = (solde) => {
    const code = String(solde?.absenceTypeCode || '').trim().toUpperCase();
    const name = String(solde?.absenceTypeNom || solde?.absenceTypeName || '').trim().toLowerCase();

    if (code === 'CP') return true;
    return (
        name.includes('congé annuel') ||
        name.includes('conges annuel') ||
        name.includes('congés annuels') ||
        name.includes('conges annuels') ||
        name.includes('congés payés') ||
        name.includes('conges payes')
    );
};


const STATUS_CONFIG = {
    todo:       { label: 'À faire',  color: '#f59e0b', bg: '#fef3c7', dot: '#f59e0b' },
    done:       { label: 'Terminé',  color: '#10b981', bg: '#d1fae5', dot: '#10b981' },
};

const LEAVE_STATUS_CONFIG = {
    PENDING: { label: 'En attente', className: 'pending' },
    EN_ATTENTE: { label: 'En attente', className: 'pending' },
    PENDING_MANAGER: { label: 'En attente', className: 'pending' },
    PENDING_ADMIN: { label: 'En attente', className: 'pending' },
    APPROVED: { label: 'Approuvé', className: 'approved' },
    APPROUVE: { label: 'Approuvé', className: 'approved' },
    REJECTED: { label: 'Refusé', className: 'refused' },
    REFUSE: { label: 'Refusé', className: 'refused' },
    CANCELLED: { label: 'Annulé', className: 'cancelled' },
    ANNULE: { label: 'Annulé', className: 'cancelled' },
    POSTPONED: { label: 'Reporté', className: 'postponed' },
    REPORTE: { label: 'Reporté', className: 'postponed' },
    POSTPONEMENT_REQUEST: { label: 'Report en attente', className: 'pending' },
};

const toDateKey = (value) => String(value || '').slice(0, 10);
const getLeaveStatus = (leave) => LEAVE_STATUS_CONFIG[String(leave?.status || leave?.statut || 'PENDING').toUpperCase()] || LEAVE_STATUS_CONFIG.PENDING;

/* ─── MINI CALENDAR ─────────────────────────────────────────── */
const DAYS_FR = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
    'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const SHORT_MONTHS_FR = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'];

const buildEmptyActivityData = (months = 9) => {
    const currentMonth = new Date();

    return Array.from({ length: months }, (_, index) => {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - months + 1 + index, 1);
        return {
            month: SHORT_MONTHS_FR[date.getMonth()],
            Connexions: 0,
            Actions: 0,
        };
    });
};

const formatLastConnection = (dateValue) => {
    if (!dateValue) return 'N/A';

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

function MiniCalendar({ onOpenAgenda, leaves }) {
    const today = new Date();
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());

    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    const leavesForDay = (day) => {
        if (!day) return [];
        const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return leaves.filter((leave) => dateKey >= toDateKey(leave.dateDebut || leave.startDate) && dateKey <= toDateKey(leave.dateFin || leave.endDate));
    };

    const prev = () => {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    };
    const next = () => {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    };

    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
        <div className="mini-calendar">
            <div className="cal-header">
                <button className="cal-nav" onClick={prev}>‹</button>
                <span
                    className="cal-month-label cal-month-clickable"
                    onClick={() => onOpenAgenda(viewYear, viewMonth)}
                    title="Voir les événements du mois"
                >
                    {MONTHS_FR[viewMonth].slice(0, 4)}. {viewYear}
                </span>
                <button className="cal-nav" onClick={next}>›</button>
            </div>
            <div className="cal-grid">
                {DAYS_FR.map((d, i) => (
                    <div key={i} className="cal-day-name">{d}</div>
                ))}
                {cells.map((day, i) => {
                    const isToday =
                        day === today.getDate() &&
                        viewMonth === today.getMonth() &&
                        viewYear === today.getFullYear();
                    const dayLeaves = leavesForDay(day);
                    const hasEvent = dayLeaves.length > 0;
                    const statusClass = hasEvent ? `leave-${getLeaveStatus(dayLeaves[0]).className}` : '';
                    return (
                        <div
                            key={i}
                            className={[
                                'cal-day',
                                !day ? 'empty' : '',
                                isToday ? 'today' : '',
                                hasEvent && day ? `has-event ${statusClass}` : ''
                            ].join(' ').trim()}
                            onClick={() => day && onOpenAgenda(viewYear, viewMonth)}
                        >
                            {day || ''}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── MODULES MODAL ──────────────────────────────────────────── */
const ModulesModal = ({ isOpen, onClose, onModuleClick, userModules }) => {
    const { t } = useTranslation();
    const fallbackModules = [
        { name: 'Formation',      icon: formationIcon },
        { name: 'Compétences',    icon: competencesIcon },
        { name: 'Performance',    icon: performanceIcon },
        { name: 'Recrutement',    icon: recrutementIcon },
        { name: 'Handbook',       icon: handbookIcon },
        { name: 'Selfcare Admin', icon: selfcareIcon },
    ];
    const displayModules = userModules && userModules.length
        ? userModules.map(m => ({ ...m, icon: formationIcon }))
        : fallbackModules;

    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><img src={modulesIcon} alt="" className="header-icon" />{t('Mes modules')}</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="modules-grid">
                    {displayModules.map((module, index) => (
                        <div key={index} className="module-card clickable"
                             onClick={() => { onClose(); onModuleClick(module); }}>
                            <img src={module.icon} alt={module.name} className="module-icon-img" />
                            <span className="module-name">{t(module.name)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ─── AGENDA MODAL ───────────────────────────────────────────── */
const AgendaModal = ({ isOpen, onClose, year, month, leaves }) => {
    const { t } = useTranslation();
    const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
    const monthLeaves = leaves.filter((leave) => toDateKey(leave.dateDebut || leave.startDate) <= monthEnd && toDateKey(leave.dateFin || leave.endDate) >= monthStart);
    const monthEvents = monthLeaves.map((leave) => {
        const status = getLeaveStatus(leave);
        return {
            id: leave.id,
            date: leave.dateDebut || leave.startDate,
            title: leave.absenceTypeNom || leave.absenceTypeName || leave.typeConge || 'Congé',
            type: status.className,
            statusLabel: status.label,
            endDate: leave.dateFin || leave.endDate,
        };
    });
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><img src={agendaIcon} alt="" className="header-icon" />
                        {t('Congés du mois')} — {MONTHS_FR[month]} {year}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="agenda-content">
                    {monthEvents.length === 0 ? (
                        <div className="agenda-empty">{t('Aucun congé ce mois-ci')}</div>
                    ) : monthEvents.map((event) => (
                        <div key={event.id} className={`agenda-event leave-event ${event.type}`}>
                            <div className="event-date">
                                <span className="event-day">{new Date(event.date).getDate()}</span>
                                <span className="event-month">
                                    {new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' })}
                                </span>
                            </div>
                            <div className="event-details">
                                <div className="event-time">Du {new Date(event.date).toLocaleDateString('fr-FR')} au {new Date(event.endDate).toLocaleDateString('fr-FR')}</div>
                                <div className="event-title">{t(event.title)}</div>
                                <span className={`leave-status ${event.type}`}>{event.statusLabel}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ─── TASK DETAIL MODAL ──────────────────────────────────────── */
const TaskDetailModal = ({ task, onClose, onStatusChange }) => {
    const { t } = useTranslation();
    if (!task) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content task-detail-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <img src={tasksIcon} alt="" className="header-icon" />
                        {t('Détail de la tâche')}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="task-detail-body">
                    <div className="task-detail-module-tag">{task.module}</div>
                    <h3 className="task-detail-title">{task.title}</h3>
                    <p className="task-detail-desc">{task.detail}</p>
                    <div className="task-detail-status-section">
                        <span className="task-detail-status-label">{t('Statut')}</span>
                        <div className="task-detail-status-pills">
                            {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                                <button
                                    key={key}
                                    className={`status-pill ${task.status === key ? 'active' : ''}`}
                                    style={task.status === key
                                        ? { background: val.bg, color: val.color, borderColor: val.color }
                                        : {}}
                                    onClick={() => onStatusChange(task.id, key)}
                                >
                                    <span className="status-pill-dot" style={{ background: val.dot }} />
                                    {val.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── CONFIRM PASSWORD MODAL ─────────────────────────────────── */
const ConfirmPasswordModal = ({ isOpen, onClose, onConfirm, title, subtitle }) => {
    const [pwd, setPwd] = useState('');
    const [show, setShow] = useState(false);
    const [error, setError] = useState('');
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        if (isOpen) { setPwd(''); setShow(false); setError(''); setTimeout(() => inputRef.current?.focus(), 80); }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (!pwd) { setError('Veuillez saisir votre mot de passe.'); return; }
        onConfirm(pwd);
    };

    return (
        <div className="modal-overlay confirm-pwd-overlay" onClick={onClose}>
            <div className="confirm-pwd-modal" onClick={e => e.stopPropagation()}>
                <div className="confirm-pwd-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                </div>
                <div className="confirm-pwd-title">{title || 'Confirmez votre identité'}</div>
                <div className="confirm-pwd-sub">{subtitle || 'Saisissez votre mot de passe pour continuer.'}</div>

                <div className="confirm-pwd-field">
                    <div className="edit-pass-wrap">
                        <input
                            ref={inputRef}
                            className="edit-field-input"
                            type={show ? 'text' : 'password'}
                            value={pwd}
                            onChange={e => { setPwd(e.target.value); setError(''); }}
                            onKeyDown={e => e.key === 'Enter' && handleConfirm()}
                            placeholder="Votre mot de passe"
                            style={error ? { borderColor: '#ef4444' } : {}}
                        />
                        <button type="button" className="edit-pass-toggle" onClick={() => setShow(v => !v)}>
                            {show ? (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                </svg>
                            ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                </svg>
                            )}
                        </button>
                    </div>
                    {error && <span className="confirm-pwd-error">{error}</span>}
                </div>

                <div className="confirm-pwd-actions">
                    <button className="edit-cancel-btn" onClick={onClose}>Annuler</button>
                    <button className="edit-save-btn" onClick={handleConfirm}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Confirmer
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── EDIT INFO MODAL ────────────────────────────────────────── */
const INITIAL_EDIT_FORM = {
    firstName: '', lastName: '', cin: '', passeport: '', carteSejour: '', birthDate: '', email: '',
    phone: '', phoneUrgence: '', adresse: '', ville: '', codePostal: '',
    statutMarital: '', nombreEnfants: '0', personneUrgence: '', rib: '', attestationRib: '', attestationRibFile: null, iban: '',
    typeContrat: '', category: '',
    currentPassword: '', newPassword: '', confirmPassword: '',
};

const EditInfoModal = ({ isOpen, onClose, onSave, user }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('info');
    const [form, setForm] = useState({ ...INITIAL_EDIT_FORM });
    const [showPasswords, setShowPasswords] = useState({ current: false, next: false, confirm: false });
    const [confirmModal, setConfirmModal] = useState({ open: false, mode: null }); // mode: 'info'

    React.useEffect(() => {
        if (isOpen && user) {
            setForm({
                firstName:       user.firstName       || '',
                lastName:        user.lastName        || '',
                cin:             user.cin             || '',
                passeport:       user.passeport       || '',
                carteSejour:     user.carteSejour     || '',
                birthDate:       user.dateOfBirth     || '',
                email:           user.email           || '',
                phone:           user.phone           || '',
                phoneUrgence:    user.phoneUrgence    || '',
                adresse:         user.adresse         || '',
                ville:           user.city            || '',
                codePostal:      user.codePostal      || '',
                statutMarital:   user.statutMarital   || '',
                nombreEnfants:   user.nombreEnfants   != null ? String(user.nombreEnfants) : '0',
                personneUrgence: user.personneUrgence || '',
                rib:             user.rib             || '',
                attestationRib:  user.attestationRib  || '',
                attestationRibFile: null,
                iban:            user.iban            || '',
                typeContrat:     user.typeContrat     || '',
                category:        user.category        || '',
                currentPassword: '', newPassword: '', confirmPassword: '',
            });
            setActiveTab('info');
            setConfirmModal({ open: false, mode: null });
        }
    }, [isOpen, user]);

    /* Password strength & rules (5 criteria) — must be before early return */
    const passwordHints = useMemo(() => {
        const p = form.newPassword || "";
        return {
            length:  p.length >= 8,
            upper:   /[A-Z]/.test(p),
            lower:   /[a-z]/.test(p),
            digit:   /[0-9]/.test(p),
            special: /[^A-Za-z0-9]/.test(p),
        };
    }, [form.newPassword]);

    const isPasswordValid = Object.values(passwordHints).every(Boolean);

    if (!isOpen) return null;

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const getStrength = (pwd) => {
        if (!pwd) return 0;
        let score = 0;
        if (passwordHints.length) score++;
        if (passwordHints.upper && passwordHints.lower) score++;
        if (passwordHints.digit) score++;
        if (passwordHints.special) score++;
        return score;
    };
    const strength = getStrength(form.newPassword);
    const strengthLabels = ['', 'Faible', 'Moyen', 'Bon', 'Excellent'];
    const strengthColors = ['', '#ef4444', '#f59e0b', '#3b82f6', '#22c55e'];

    /* ── open confirm modal then save ── */
    const requestSaveInfo = () => setConfirmModal({ open: true, mode: 'info' });
    const requestSavePassword = () => {
        if (!form.currentPassword || !isPasswordValid || form.newPassword !== form.confirmPassword) return;
        // On soumet directement car le mot de passe actuel est déjà saisi dans l'onglet
        onSave({ ...INITIAL_EDIT_FORM, currentPassword: form.currentPassword, newPassword: form.newPassword, confirmPassword: form.confirmPassword });
        onClose();
    };

    const handleConfirmed = (currentPassword) => {
        setConfirmModal({ open: false, mode: null });
        if (confirmModal.mode === 'info') {
            onSave({ ...form, currentPassword, newPassword: '', confirmPassword: '' });
        }
        onClose();
    };

    const togglePass = (key) => setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));

    const EyeIcon = ({ open }) => open ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
    ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
        </svg>
    );

    return (
        <>
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content edit-info-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--a-deep)' }}>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        {t('Modifier mes informations')}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                {/* ── TABS ── */}
                <div className="edit-info-tabs">
                    <button
                        className={`edit-info-tab ${activeTab === 'info' ? 'active' : ''}`}
                        onClick={() => setActiveTab('info')}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                        </svg>
                        Informations personnelles
                    </button>
                    <button
                        className={`edit-info-tab ${activeTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveTab('security')}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                        Sécurité & Mot de passe
                    </button>
                </div>

                <div className="edit-info-body">
                    {/* ── TAB: INFORMATIONS ── */}
                    {activeTab === 'info' && (<>
                        <div className="edit-section-title">Identité</div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">Prénom</label>
                                <input className="edit-field-input" type="text" value={form.firstName}
                                       onChange={e => set('firstName', e.target.value)} placeholder="Prénom"/>
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">Nom</label>
                                <input className="edit-field-input" type="text" value={form.lastName}
                                       onChange={e => set('lastName', e.target.value)} placeholder="Nom"/>
                            </div>
                        </div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">CIN</label>
                                <input className="edit-field-input" type="text" value={form.cin}
                                       onChange={e => set('cin', e.target.value)} placeholder="CIN"/>
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">Date de naissance</label>
                                <input className="edit-field-input" type="date" value={form.birthDate}
                                       onChange={e => set('birthDate', e.target.value)}/>
                            </div>
                        </div>

                        <div className="edit-section-title">Contact</div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">Email</label>
                                <input className="edit-field-input" type="email" value={form.email}
                                       onChange={e => set('email', e.target.value)} placeholder="Email"/>
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">Téléphone</label>
                                <input className="edit-field-input" type="tel" value={form.phone}
                                       onChange={e => set('phone', e.target.value)} placeholder="Téléphone"/>
                            </div>
                        </div>
                        <div className="edit-field-group">
                            <label className="edit-field-label">Adresse</label>
                            <input className="edit-field-input" type="text" value={form.adresse}
                                   onChange={e => set('adresse', e.target.value)} placeholder="Adresse"/>
                        </div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">Ville</label>
                                <input className="edit-field-input" type="text" value={form.ville}
                                       onChange={e => set('ville', e.target.value)} placeholder="Ville"/>
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">Code postal</label>
                                <input className="edit-field-input" type="text" value={form.codePostal}
                                       onChange={e => set('codePostal', e.target.value)} placeholder="Code postal"/>
                            </div>
                        </div>

                        <div className="edit-section-title">Situation personnelle</div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">Statut marital</label>
                                <select className="edit-field-input edit-field-select" value={form.statutMarital}
                                        onChange={e => set('statutMarital', e.target.value)}>
                                    <option value="">-- Sélectionner --</option>
                                    <option value="celibataire">Célibataire</option>
                                    <option value="marie">Marié(e)</option>
                                    <option value="divorce">Divorcé(e)</option>
                                    <option value="veuf">Veuf/Veuve</option>
                                    <option value="pacse">Pacsé(e)</option>
                                </select>
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">Nombre d'enfants</label>
                                <input className="edit-field-input" type="number" min="0" value={form.nombreEnfants}
                                       onChange={e => set('nombreEnfants', e.target.value)}/>
                            </div>
                        </div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">Personne à contacter en urgence</label>
                                <input className="edit-field-input" type="text" value={form.personneUrgence}
                                       onChange={e => set('personneUrgence', e.target.value)}
                                       placeholder="Nom & prénom"/>
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">Téléphone d'urgence</label>
                                <input className="edit-field-input" type="tel" value={form.phoneUrgence}
                                       onChange={e => set('phoneUrgence', e.target.value)}
                                       placeholder="Téléphone d'urgence"/>
                            </div>
                        </div>

                        <div className="edit-section-title">Informations bancaires</div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">Attestation de RIB</label>
                                <input className="edit-field-input" type="file" accept="application/pdf,.pdf"
                                       onChange={e => set('attestationRibFile', e.target.files?.[0] || null)}/>
                                {form.attestationRib && !form.attestationRibFile && (
                                    <small>{form.attestationRib}</small>
                                )}
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">RIB</label>
                                <input className="edit-field-input" type="text" value={form.rib}
                                       onChange={e => set('rib', e.target.value)} placeholder="RIB"/>
                            </div>
                        </div>
                        <div className="edit-field-group">
                            <label className="edit-field-label">IBAN</label>
                            <input className="edit-field-input" type="text" value={form.iban}
                                   onChange={e => set('iban', e.target.value)} placeholder="IBAN"/>
                        </div>

                        <div className="edit-section-title">Informations contractuelles</div>
                        <div className="edit-row-2">
                            <div className="edit-field-group">
                                <label className="edit-field-label">Type de contrat</label>
                                <select className="edit-field-input edit-field-select" value={form.typeContrat}
                                        onChange={e => set('typeContrat', e.target.value)}>
                                    <option value="">-- Sélectionner --</option>
                                    <option value="CDI">CDI</option>
                                    <option value="CDD">CDD</option>
                                </select>
                            </div>
                            <div className="edit-field-group">
                                <label className="edit-field-label">Catégorie</label>
                                <select className="edit-field-input edit-field-select" value={form.category}
                                        onChange={e => set('category', e.target.value)}>
                                    <option value="">-- Sélectionner --</option>
                                    <option value="POSTE_FIXE">Poste fixe</option>
                                    <option value="MENSUEL">Mensuel</option>
                                    <option value="HORAIRE">Horaire</option>
                                </select>
                            </div>
                        </div>

                        <div className="edit-info-actions">
                            <button className="edit-cancel-btn" onClick={onClose}>{t('Annuler')}</button>
                            <button className="edit-save-btn" onClick={requestSaveInfo}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                {t('Enregistrer')}
                            </button>
                        </div>
                    </>)}

                    {/* ── TAB: SÉCURITÉ ── */}
                    {activeTab === 'security' && (<>
                        <div className="edit-security-hero">
                            <div className="edit-security-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                </svg>
                            </div>
                            <div>
                                <div className="edit-security-title">Changer le mot de passe</div>
                                <div className="edit-security-desc">Saisissez votre mot de passe actuel puis choisissez un nouveau mot de passe robuste.</div>
                            </div>
                        </div>

                        <div className="edit-field-group">
                            <label className="edit-field-label">Mot de passe actuel</label>
                            <div className="edit-pass-wrap">
                                <input className="edit-field-input" type={showPasswords.current ? 'text' : 'password'}
                                       value={form.currentPassword} onChange={e => set('currentPassword', e.target.value)}
                                       placeholder="Saisissez votre mot de passe actuel"/>
                                <button type="button" className="edit-pass-toggle" onClick={() => togglePass('current')}>
                                    <EyeIcon open={showPasswords.current}/>
                                </button>
                            </div>
                        </div>

                        <div className="edit-field-group">
                            <label className="edit-field-label">Nouveau mot de passe</label>
                            <div className="edit-pass-wrap">
                                <input className="edit-field-input" type={showPasswords.next ? 'text' : 'password'}
                                       value={form.newPassword} onChange={e => set('newPassword', e.target.value)}
                                       placeholder="Au moins 8 caractères"/>
                                <button type="button" className="edit-pass-toggle" onClick={() => togglePass('next')}>
                                    <EyeIcon open={showPasswords.next}/>
                                </button>
                            </div>
                            {form.newPassword && (
                                <div className="edit-pass-strength">
                                    <div className="edit-pass-strength-bars">
                                        {[1,2,3,4].map(i => (
                                            <div key={i} className="edit-pass-strength-bar"
                                                 style={{ background: i <= strength ? strengthColors[strength] : 'var(--border)' }}/>
                                        ))}
                                    </div>
                                    <span className="edit-pass-strength-label" style={{ color: strengthColors[strength] }}>
                                        {strengthLabels[strength]}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="edit-field-group">
                            <label className="edit-field-label">Confirmer le nouveau mot de passe</label>
                            <div className="edit-pass-wrap">
                                <input className="edit-field-input"
                                       type={showPasswords.confirm ? 'text' : 'password'}
                                       value={form.confirmPassword}
                                       onChange={e => set('confirmPassword', e.target.value)}
                                       placeholder="Répétez le mot de passe"
                                       style={form.confirmPassword && form.newPassword !== form.confirmPassword
                                           ? { borderColor: '#ef4444' } : {}}/>
                                <button type="button" className="edit-pass-toggle" onClick={() => togglePass('confirm')}>
                                    <EyeIcon open={showPasswords.confirm}/>
                                </button>
                            </div>
                            {form.confirmPassword && form.newPassword !== form.confirmPassword && (
                                <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                                    Les mots de passe ne correspondent pas
                                </span>
                            )}
                            {form.confirmPassword && form.newPassword === form.confirmPassword && form.newPassword && (
                                <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>
                                    ✓ Les mots de passe correspondent
                                </span>
                            )}
                        </div>

                        <div className="edit-pass-rules">
                            <div className="edit-pass-rule" style={{ color: passwordHints.length ? '#22c55e' : 'var(--muted)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    {passwordHints.length ? <polyline points="20 6 9 17 4 12"/> : <circle cx="12" cy="12" r="8"/>}
                                </svg>
                                Au moins 8 caractères
                            </div>
                            <div className="edit-pass-rule" style={{ color: passwordHints.upper ? '#22c55e' : 'var(--muted)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    {passwordHints.upper ? <polyline points="20 6 9 17 4 12"/> : <circle cx="12" cy="12" r="8"/>}
                                </svg>
                                Une majuscule (A-Z)
                            </div>
                            <div className="edit-pass-rule" style={{ color: passwordHints.lower ? '#22c55e' : 'var(--muted)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    {passwordHints.lower ? <polyline points="20 6 9 17 4 12"/> : <circle cx="12" cy="12" r="8"/>}
                                </svg>
                                Une minuscule (a-z)
                            </div>
                            <div className="edit-pass-rule" style={{ color: passwordHints.digit ? '#22c55e' : 'var(--muted)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    {passwordHints.digit ? <polyline points="20 6 9 17 4 12"/> : <circle cx="12" cy="12" r="8"/>}
                                </svg>
                                Un chiffre (0-9)
                            </div>
                            <div className="edit-pass-rule" style={{ color: passwordHints.special ? '#22c55e' : 'var(--muted)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    {passwordHints.special ? <polyline points="20 6 9 17 4 12"/> : <circle cx="12" cy="12" r="8"/>}
                                </svg>
                                Un caractère spécial (!@#$...)
                            </div>
                        </div>

                        <div className="edit-info-actions">
                            <button className="edit-cancel-btn" onClick={onClose}>{t('Annuler')}</button>
                            <button className="edit-save-btn"
                                    disabled={!form.currentPassword || !isPasswordValid || form.newPassword !== form.confirmPassword}
                                    onClick={requestSavePassword}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                                Changer le mot de passe
                            </button>
                        </div>
                    </>)}
                </div>
            </div>
        </div>

        {/* ── CONFIRM PASSWORD MINI-MODAL ── */}
        <ConfirmPasswordModal
            isOpen={confirmModal.open}
            onClose={() => setConfirmModal({ open: false, mode: null })}
            onConfirm={handleConfirmed}
            title="Confirmez votre identité"
            subtitle={
                confirmModal.mode === 'password'
                    ? 'Saisissez votre mot de passe actuel pour valider le changement.'
                    : 'Saisissez votre mot de passe pour enregistrer les modifications.'
            }
        />
        </>
    );
};




/* ─── EDIT PHOTO MODAL ───────────────────────────────────────── */
const EditPhotoModal = ({isOpen, onClose, currentPhoto, onSave, onDelete, title, subtitle, avatarInitials}) => {
    const {t} = useTranslation();
    const [preview, setPreview] = useState(null);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const loading = useSelector(state => state.user.loading);
    React.useEffect(() => {
        if (isOpen) { setPreview(null); setSelectedFile(null); }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content edit-photo-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--a-deep)', marginRight: 6 }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                        </svg>
                        {title || t('Modifier la photo')}
                    </h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="edit-photo-body">
                    {subtitle && <p className="edit-photo-subtitle">{subtitle}</p>}
                    <div className="edit-photo-preview-wrap">
                        {preview || currentPhoto ? (
                            <img src={preview || currentPhoto} alt="Preview" className="edit-photo-preview" />
                        ) : (
                            <div className="edit-photo-preview edit-photo-preview-avatar">
                                {avatarInitials}
                            </div>
                        )}
                        {preview && (
                            <div className="edit-photo-preview-badge">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Nouvelle photo
                            </div>
                        )}
                    </div>
                    <div
                        className={`edit-photo-dropzone ${dragging ? 'dragging' : ''}`}
                        onDragOver={e => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="16 16 12 12 8 16"/>
                            <line x1="12" y1="12" x2="12" y2="21"/>
                            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                        </svg>
                        <span className="dropzone-main-text">
                            {dragging ? 'Relâchez pour importer' : 'Glissez une image ici'}
                        </span>
                        <span className="dropzone-sub-text">ou <strong>cliquez pour parcourir</strong> · JPG, PNG, WebP</span>
                        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                               onChange={e => handleFile(e.target.files[0])} />
                    </div>
                    <div className="edit-info-actions">
                        {onDelete && currentPhoto && (
                            <button
                                type="button"
                                className="edit-delete-btn"
                                disabled={loading}
                                onClick={() => {
                                    onDelete();
                                    onClose();
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                                {t('Supprimer')}
                            </button>
                        )}
                        <button className="edit-save-btn" disabled={!selectedFile || loading}
                                onClick={() => { if (selectedFile) { onSave(selectedFile); onClose(); } }}>
                            {loading ? 'Envoi...' : t('Enregistrer')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── TASKS SECTION ──────────────────────────────────────────── */
const TasksSection = ({ onTaskClick }) => {
    const { t } = useTranslation();
    const [tasks, setTasks] = useState(INITIAL_TASKS);
    const [filter, setFilter] = useState('all');

    const toggleDone = (id) => {
        setTasks(prev => prev.map(tk =>
            tk.id === id ? { ...tk, status: tk.status === 'done' ? 'todo' : 'done' } : tk
        ));
    };

    const filters = [
        { key: 'all',        label: 'Toutes' },
        { key: 'todo',       label: 'À faire' },
        { key: 'done',       label: 'Terminées' },
    ];

    const filtered = filter === 'all' ? tasks : tasks.filter(tk => tk.status === filter);
    const doneCount = tasks.filter(tk => tk.status === 'done').length;

    return (
        <div className="sidebar-section tasks-section">
            <div className="section-header">
                <h3>
                    <img src={tasksIcon} alt="" className="section-icon" />
                    {t('Mes tâches')}
                </h3>
                <div className="tasks-header-right">
                    <span className="tasks-progress-text">{doneCount}/{tasks.length}</span>
                    <span className="badge-count">{tasks.filter(tk => tk.status !== 'done').length}</span>
                </div>
            </div>
            <div className="tasks-progress-bar">
                <div className="tasks-progress-fill" style={{ width: `${tasks.length > 0 ? Math.round((doneCount / tasks.length) * 100) : 0}%` }} />
            </div>
            <div className="tasks-filter-row">
                {filters.map(f => (
                    <button key={f.key} className={`task-filter-pill ${filter === f.key ? 'active' : ''}`}
                            onClick={() => setFilter(f.key)}>{f.label}</button>
                ))}
            </div>
            <div className="tasks-list">
                {filtered.length === 0 && <div className="tasks-empty">{tasks.length === 0 ? t('Aucune tâche associée') : t('Aucune tâche dans cette catégorie')}</div>}
                {filtered.map(task => {
                    const cfg = STATUS_CONFIG[task.status];
                    return (
                        <div key={task.id} className={`task-row ${task.status === 'done' ? 'task-done' : ''}`}>
                            <button className={`task-checkbox ${task.status === 'done' ? 'checked' : ''}`}
                                    onClick={() => toggleDone(task.id)}
                                    title={task.status === 'done' ? 'Marquer non terminé' : 'Marquer terminé'}>
                                {task.status === 'done' && (
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </button>
                            <div className="task-row-content" onClick={() => onTaskClick(task)}>
                                <span className="task-row-title">{task.title}</span>
                                <span className="task-row-status" style={{ color: cfg.color, background: cfg.bg }}>
                                    <span className="task-status-dot" style={{ background: cfg.dot }} />
                                    {cfg.label}
                                </span>
                            </div>
                            <button className="task-detail-btn" onClick={() => onTaskClick(task)} title="Voir le détail">›</button>
                        </div>

                    );
                })}
            </div>
        </div>
    );
};

/* ─── EDIT ICON SVG ──────────────────────────────────────────── */
const EditIcon = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
const AuthCards = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authUser = useSelector(state => state.auth.user);
    const fetchedUser = useSelector(state => state.user.currentUser);
    const user = fetchedUser?.id && String(fetchedUser.id) === String(authUser?.id)
        ? { ...authUser, ...fetchedUser }
        : authUser || fetchedUser;
    const loading = useSelector(state => state.user.loading);
    const myLeaveRequests = useSelector(state => state.leaveRequests?.myRequests || state.leaveRequest?.myRequests || []);
    const { userSoldes = [], loading: balanceLoading } = useSelector((state) => state.leaveBalance || {});
    const {
        myActivitySummary = [],
        myActivitySummaryLoading = false,
    } = useSelector((state) => state.applicationLog || {});


    const userId = user?.id || sessionStorage.getItem("uid");
    const companyId = user?.companyId || sessionStorage.getItem("companyId");

    useEffect(() => {
        if (userId && userId !== "0" && userId !== 0) {
            dispatch(getUserRequest(userId));
            dispatch(getMyActivitySummaryRequest(9));
            dispatch(getMyLeaveRequestsRequest());
        }
    }, [dispatch, userId]);


    const today = new Date();
    const [activeTab, setActiveTab] = useState('module');
    const [isModulesModalOpen, setIsModulesModalOpen] = useState(false);
    const [isAgendaModalOpen, setIsAgendaModalOpen] = useState(false);
    const [agendaModalYear, setAgendaModalYear] = useState(today.getFullYear());
    const [agendaModalMonth, setAgendaModalMonth] = useState(today.getMonth());
    const [selectedTask, setSelectedTask] = useState(null);

    // ── Edit modals state
    const [isEditInfoOpen, setIsEditInfoOpen] = useState(false);
    const [isEditProfilePhotoOpen, setIsEditProfilePhotoOpen] = useState(false);
    const [isEditCoverOpen, setIsEditCoverOpen] = useState(false);

    // ── Local editable user info
    const [localUserInfo, setLocalUserInfo] = useState({
        jobName:         user?.jobName         || 'Directrice commerciale',
        seniority:       user?.seniority       || '6 ans 4 mois',
        entityName:      user?.entityName      || 'Direction commerciale',
        congeBalance:    '32 jours',
    });

    const activityData = useMemo(() => {
        if (!myActivitySummary.length) return buildEmptyActivityData(9);

        return myActivitySummary.map(item => ({
            month: item.month,
            Connexions: Number(item.connexions || 0),
            Actions: Number(item.actions || 0),
        }));
    }, [myActivitySummary]);

    useEffect(() => {
        const isValidUser = userId && userId !== "0" && userId !== 0;
        const isValidCompany = companyId && companyId !== "0" && companyId !== 0;
        if (isValidUser && isValidCompany) {
            dispatch(getUserSoldesRequest({ userId, companyId }));
        }
    }, [userId, companyId]);

    const soldePrincipal = useMemo(() => {
        if (!userSoldes.length) return null;
        const annualBalance = userSoldes.find(isAnnualLeaveBalance);
        const selectedSolde = annualBalance || userSoldes.find((s) => {
            const name = String(s?.absenceTypeNom || s?.absenceTypeName || '').toLowerCase();
            return !name.includes('sans solde');
        }) || userSoldes[0];

        return {
            type: selectedSolde.absenceTypeNom || selectedSolde.absenceTypeName || "Congé annuel",
            restant: Number(selectedSolde.soldeRestant || 0),
        };
    }, [userSoldes]);


    const hasProfilePhoto = !!(user?.profilePictureUrl && String(user.profilePictureUrl).trim());
    const profilePhotoSrc = hasProfilePhoto
        ? `${API_BASE_URL}/api/users/images/profile/${user.profilePictureUrl}`
        : null;

    const bannerSrc = user && user.bannerPictureUrl
        ? `${API_BASE_URL}/api/users/images/banner/${user.bannerPictureUrl}`
        : coverImage;

    const openAgenda = (year, month) => {
        setAgendaModalYear(year);
        setAgendaModalMonth(month);
        setIsAgendaModalOpen(true);
    };

    const handleSaveProfile = (formData) => {
        if (!user || !user.id) return;
        dispatch(updateMyProfileRequest(user.id, {
            firstName:       formData.firstName       || null,
            lastName:        formData.lastName        || null,
            phone:           formData.phone           || null,
            dateOfBirth:     formData.birthDate       || null,
            city:            formData.ville           || null,
            bio:             null,
            cin:             formData.cin             || null,
            passeport:       formData.passeport       || null,
            carteSejour:     formData.carteSejour     || null,
            phoneUrgence:    formData.phoneUrgence    || null,
            adresse:         formData.adresse         || null,
            codePostal:      formData.codePostal      || null,
            statutMarital:   formData.statutMarital   || null,
            nombreEnfants:   formData.nombreEnfants   ? parseInt(formData.nombreEnfants) : null,
            personneUrgence: formData.personneUrgence || null,
            rib:             formData.rib             || null,
            attestationRib:  formData.attestationRib  || null,
            iban:            formData.iban            || null,
            typeContrat:     formData.typeContrat     || null,
            category:        formData.category        || null,
            currentPassword: formData.currentPassword || null,
            newPassword:     formData.newPassword     || null,
            confirmPassword: formData.confirmPassword || null,
        }));
        if (formData.attestationRibFile) {
            dispatch(uploadAttestationRibRequest(user.id, formData.attestationRibFile));
        }
        setIsEditInfoOpen(false);
    };

    const handleSaveProfilePhoto = (file) => {
        if (!user || !user.id) return;
        dispatch(uploadUserPhotoRequest(user.id, file, 'profile'));
    };

    const handleDeleteProfilePhoto = () => {
        if (!user || !user.id) return;
        dispatch(deleteUserPhotoRequest(user.id, 'profile'));
    };

    const handleSaveBanner = (file) => {
        if (!user || !user.id) return;
        dispatch(uploadUserPhotoRequest(user.id, file, 'banner'));
    };

    const handleDeleteBanner = () => {
        if (!user || !user.id) return;
        dispatch(deleteUserPhotoRequest(user.id, 'banner'));
    };

    const MODULE_ROUTES = {
        "Selfcare Admin": "/collaborateur/dashboard",
        "Skills": "/skills/dashboard/collaborateur",
        "Compétences": "/skills/dashboard/collaborateur",
        "Formation": "/formation/dashboard/collaborateur",
        "Performance": "/performance/dashboard/collaborateur",
        "Recrutement": "/recrutement/dashboard/collaborateur",
        "Handbook": "/handbook/dashboard/collaborateur",
    };

    const getModulePath = (m) => {
        const byCode = {
            SELFCARE: "/collaborateur/dashboard",
            SKILLS: "/skills/dashboard/collaborateur",
            FORMATION: "/formation/dashboard/collaborateur",
            PERF: "/performance/dashboard/collaborateur",
            RECRUTEMENT: "/recrutement/dashboard/collaborateur",
            HANDBOOK: "/handbook/dashboard/collaborateur",
        };
        return byCode[(m?.code || '').trim()] ||
            MODULE_ROUTES[(m?.name || '').trim()] ||
            "/collaborateur/dashboard";
    };

    const handleModuleClick = (module) => {
        const moduleRoles = user?.roles?.filter(r => r.moduleId === module.id && r.isActive !== false) || [];
        const defaultRole = moduleRoles.find(r => r.isDefault === true) || moduleRoles[0];
        if (defaultRole) {
            sessionStorage.setItem("roleChanged", "true");
            dispatch(setSelectedRole(defaultRole));
        }
        navigate(getModulePath(module));
    };

    const getUserModules = (u) => {
        if (!u?.roles || !Array.isArray(u.roles)) return [];
        const map = new Map();
        u.roles.forEach(role => {
            if (role.moduleId && role.moduleName) {
                const existing = map.get(role.moduleId);
                const isPreferredRole = role.isDefault === true && existing?.isDefault !== true;
                if (!existing || isPreferredRole) {
                    const normalizedName = String(role.moduleName || '')
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .trim()
                        .toUpperCase();
                    const moduleCode = normalizedName.includes('FORMATION') ? 'FORMATION'
                        : normalizedName.includes('COMPETENCE') || normalizedName.includes('SKILL') ? 'SKILLS'
                        : normalizedName.includes('PERFORMANCE') ? 'PERF'
                        : normalizedName.includes('RECRUTEMENT') ? 'RECRUTEMENT'
                        : normalizedName.includes('HANDBOOK') ? 'HANDBOOK'
                        : normalizedName.includes('SELFCARE') || normalizedName.includes('CORE RH') ? 'SELFCARE'
                        : '';
                    map.set(role.moduleId, {
                        id: role.moduleId,
                        name: role.moduleName,
                        code: moduleCode,
                        roleName: role.name,
                        isDefault: role.isDefault === true,
                    });
                }
            }
        });
        return Array.from(map.values());
    };

    const userModules = getUserModules(user);
    const shownModules = userModules.length
        ? userModules.slice(0, 2)
        : [
            { id: 'f1', name: 'Formation', code: 'FORMATION' },
            { id: 'f2', name: 'Performance', code: 'PERF' },
        ];

    const badges = [
        { type: 'silver', img: silverBadge },
        { type: 'silver', img: silverBadge },
        { type: 'gold',   img: goldBadge },
    ];

    const navigationByModule = [
        { name: 'Formation',   value: 80 },
        { name: 'Recrutement', value: 90 },
        { name: 'Performance', value: 65 },
        { name: 'Compétences', value: 85 },
        { name: 'Selfcare',    value: 50 },
    ];

    const navigationByMonth = [
        { month: 'Jan', Formation: 12, Compétences: 10, Recrutement: 9,  Performance: 7,  Handbook: 6,  'Selfcare Admin': 4 },
        { month: 'Fév', Formation: 15, Compétences: 12, Recrutement: 11, Performance: 8,  Handbook: 7,  'Selfcare Admin': 5 },
        { month: 'Mar', Formation: 18, Compétences: 14, Recrutement: 13, Performance: 6,  Handbook: 6,  'Selfcare Admin': 5 },
        { month: 'Avr', Formation: 14, Compétences: 15, Recrutement: 15, Performance: 10, Handbook: 5,  'Selfcare Admin': 4 },
        { month: 'Mai', Formation: 19, Compétences: 13, Recrutement: 10, Performance: 9,  Handbook: 6,  'Selfcare Admin': 6 },
        { month: 'Juin', Formation: 21, Compétences: 17, Recrutement: 14, Performance: 10, Handbook: 9, 'Selfcare Admin': 8 },
        { month: 'Juil', Formation: 18, Compétences: 14, Recrutement: 13, Performance: 10, Handbook: 7, 'Selfcare Admin': 6 },
        { month: 'Août', Formation: 20, Compétences: 16, Recrutement: 15, Performance: 12, Handbook: 10,'Selfcare Admin': 9 },
        { month: 'Sep',  Formation: 17, Compétences: 13, Recrutement: 14, Performance: 9,  Handbook: 7, 'Selfcare Admin': 6 },
    ];

    const thisMonthCount = myLeaveRequests.filter((leave) => {
        const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
        const monthEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
        return toDateKey(leave.dateDebut || leave.startDate) <= monthEnd && toDateKey(leave.dateFin || leave.endDate) >= monthStart;
    }).length;

    const renderBarLabel = ({ x, y, width, value }) => (
        <text x={x + width / 2} y={y - 5} fill="#1e3a8a"
              textAnchor="middle" fontSize="12" fontWeight="600">{value}%</text>
    );

    const firstName = user?.firstName || user?.fullName?.split(' ')[0] || 'Emma';
    const lastName  = user?.lastName  || 'SABRI';
    const profileInitials = getInitials(user?.firstName || firstName, user?.lastName || lastName, user?.fullName);

    return (
        <div className="auth-cards-page">
            <UpperBarIn />
            <div className="auth-cards-content">

                {/* ── BANNER ── */}
                <div className="banner-wrapper">
                    <img src={bannerSrc} alt="Cover" className="cover-image" />

                    <button className="banner-edit-btn" onClick={() => setIsEditCoverOpen(true)}
                            title="Modifier la photo de couverture">
                        <EditIcon size={13} color="white" />
                        <span>Modifier la bannière</span>
                    </button>

                    <div className="profile-image-wrap" onClick={() => setIsEditProfilePhotoOpen(true)}>
                        <div className="profile-image-ring">
                            {hasProfilePhoto ? (
                                <img src={profilePhotoSrc} alt="Profile" className="profile-image" />
                            ) : (
                                <div className="profile-image profile-avatar" aria-label={`Avatar ${profileInitials}`}>
                                    {profileInitials}
                                </div>
                            )}
                        </div>
                        <div className="profile-photo-edit-overlay" title="Modifier la photo de profil">
                            <EditIcon size={15} color="white" />
                        </div>
                    </div>

                    <div className="banner-content">
                        <div className="profile-info">
                            <h1>{t('Bonjour,')} <span className="highlight-name">{firstName}</span> !</h1>
                            <p className="profile-role">{user?.jobName || 'Directrice commerciale'}</p>
                        </div>
                        <div className="last-connection">
                            {t('Dernière connexion')} · {formatLastConnection(user?.lastLoginDate)}
                        </div>
                    </div>
                </div>

                {/* ── 4-COLUMN GRID ── */}
                <div className="columns-container">

                    {/* COL 1 — Infos collaborateur */}
                    <div className="column column-1">
                        <div className="col1-header">
                            <div className="emp-name">{firstName} {lastName}</div>
                            <button className="col1-edit-btn" onClick={() => setIsEditInfoOpen(true)}
                                    title="Modifier les informations">
                                <EditIcon size={13} />
                            </button>
                        </div>
                        {[
                            { label: t('Poste'),          value: user?.jobName || localUserInfo.jobName },
                            { label: t('Ancienneté'),     value: user?.seniority || localUserInfo.seniority },
                            { label: t('Direction'),      value: user?.entityName || localUserInfo.entityName },
                            { label: t('Manager'),        value: getManagerDisplayName(user) },
                            { label: t('Type de contrat'),   value: user?.typeContrat || localUserInfo.typeContrat },

                            {
                                label: t('Solde de congé'),
                                value: balanceLoading
                                    ? '...'
                                    : soldePrincipal
                                        ? `${soldePrincipal.restant} jours (${soldePrincipal.type})`
                                        : 'N/A',
                            },

                        ].map(({ label, value }) => (

                            <div key={label} className="info-row">
                                <span className="info-label">{label}</span>
                                <span className="info-value">{value}</span>
                            </div>
                        ))}
                    </div>

                    {/* COL 2 — Activité RH */}
                    <div className="column column-2">
                        <div className="navigation-tabs">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: 2 }}>
                                <span style={{
                                    fontFamily: 'DM Sans, sans-serif',
                                    fontSize: 13, fontWeight: 700,
                                    color: 'var(--navy)', letterSpacing: '-0.01em'
                                }}>
                                    Mon activité RH
                                </span>
                                <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>
                                    {myActivitySummaryLoading ? 'Chargement de votre activité...' : 'Connexions & actions sur la plateforme'}
                                </span>
                            </div>
                        </div>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={activityData} margin={{ top: 16, right: 20, left: -10, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="month" tick={{ fill: '#4b5563', fontSize: 11 }} />
                                    <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} allowDecimals={false} domain={[0, 'auto']} />
                                    <Tooltip
                                        contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8 }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="line" />
                                    <Line
                                        type="monotone" dataKey="Connexions"
                                        stroke="#2a5ca9" strokeWidth={2.5}
                                        dot={{ r: 3 }} activeDot={{ r: 5 }}
                                    />
                                    <Line
                                        type="monotone" dataKey="Actions"
                                        stroke="#6e91c1" strokeWidth={2.5}
                                        dot={{ r: 3 }} activeDot={{ r: 5 }}
                                        strokeDasharray="5 3"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* COL 3 — Agenda + CTA */}
                    <div className="column column-3">
                        {/* Bouton CTA — accès direct au module unique */}
                        <button
                            onClick={() => handleModuleClick(shownModules[0])}
                            style={{
                                width: '100%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                gap: 8,
                                padding: '13px 16px',
                                background: 'linear-gradient(135deg, #2a5ca9 0%, #3a70c4 100%)',
                                border: 'none', borderRadius: 14,
                                color: 'white',
                                fontFamily: 'DM Sans, sans-serif',
                                fontSize: 14, fontWeight: 700,
                                cursor: 'pointer',
                                letterSpacing: '0.02em',
                                boxShadow: '0 4px 16px rgba(42,92,169,0.28)',
                                transition: 'all 0.22s ease',
                                flexShrink: 0,
                                marginTop:'8px'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(42,92,169,0.40)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(42,92,169,0.28)';
                            }}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                 stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                                <polyline points="9 22 9 12 15 12 15 22"/>
                            </svg>
                            Accéder à mon espace personnel
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                 stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </button>

                        {/* Agenda */}
                        <div className="sidebar-section" id="mon-agenda-card">
                            <div className="section-header">

                                <h3>
                                    <img src={agendaIcon} alt="" className="section-icon" />
                                    {t('Mon Agenda')}
                                </h3>
                                <span
                                    className="badge-count badge-count-clickable"
                                    onClick={() => openAgenda(today.getFullYear(), today.getMonth())}
                                    title={t('Voir les événements du mois')}
                                >
                                    {thisMonthCount}
                                </span>
                            </div>
                            <MiniCalendar onOpenAgenda={openAgenda} leaves={myLeaveRequests} />
                        </div>
                    </div>


                    {/* COL 4 — Tasks */}
                    <div className="column column-4">
                        <TasksSection onTaskClick={setSelectedTask} />
                    </div>
                </div>
            </div>

            {/* ── MODALS ── */}
            <ModulesModal
                isOpen={isModulesModalOpen}
                onClose={() => setIsModulesModalOpen(false)}
                onModuleClick={handleModuleClick}
                userModules={userModules}
            />
            <AgendaModal
                isOpen={isAgendaModalOpen}
                onClose={() => setIsAgendaModalOpen(false)}
                year={agendaModalYear}
                month={agendaModalMonth}
                leaves={myLeaveRequests}
            />
            {selectedTask && (
                <TaskDetailModal
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                    onStatusChange={(id, newStatus) => {
                        setSelectedTask(prev => prev && prev.id === id ? { ...prev, status: newStatus } : prev);
                    }}
                />
            )}

            <EditInfoModal
                isOpen={isEditInfoOpen}
                onClose={() => setIsEditInfoOpen(false)}
                onSave={handleSaveProfile}
                loading={loading}
                user={user}
            />

            <EditPhotoModal
                isOpen={isEditProfilePhotoOpen}
                onClose={() => setIsEditProfilePhotoOpen(false)}
                currentPhoto={profilePhotoSrc}
                avatarInitials={profileInitials}
                onSave={handleSaveProfilePhoto}
                onDelete={hasProfilePhoto ? handleDeleteProfilePhoto : null}
                title="Modifier la photo de profil"
                subtitle="Choisissez une photo carrée pour un meilleur rendu."
                loading={loading}
            />

            <EditPhotoModal
                isOpen={isEditCoverOpen}
                onClose={() => setIsEditCoverOpen(false)}
                currentPhoto={user?.bannerPictureUrl ? bannerSrc : null}
                onSave={handleSaveBanner}
                onDelete={user?.bannerPictureUrl ? handleDeleteBanner : null}
                title="Modifier la photo de couverture"
                subtitle="Recommandé : image au format 16:9, minimum 1200×400px."
                loading={loading}
            />
        </div>
    );
};

export default AuthCards;
